import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { brandAliasesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { Brand } from '@rock/types';
import { lineNameFromAlias } from '@rock/brands';
import { normalizeBrandName } from '@rock/util';
import { Plus, SplitSquareHorizontal, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

interface BrandAliasesTabProps {
    brand: Brand;
    /** A merged brand's names belong to its winner now — show them, change nothing. */
    readOnly?: boolean;
    onAddAlias: (aliasName: string) => Promise<void>;
    onRemoveAlias: (aliasName: string) => Promise<void>;
    /** "HUNTER BRUNSWICK" on Hunter Fan is the Brunswick line, not an alias: convert it. */
    onAliasToLine: (aliasName: string) => Promise<void>;
}

/**
 * The brand's alternate names. Rows come from `brand.aliases` (the human-readable list); the
 * "Resolvable keys" section shows the actual `brand-aliases` lookup docs, OCR variants included —
 * what the server and scrapers can resolve.
 */
export const BrandAliasesTab: FC<BrandAliasesTabProps> = ({
    brand,
    readOnly,
    onAddAlias,
    onRemoveAlias,
    onAliasToLine,
}) => {
    const aliasDocs = useAsyncAtomValue(
        useMemo(() => brandAliasesState(brand.id), [brand.id]),
        [],
    );
    const [aliasInput, setAliasInput] = useState('');
    const [isMutating, setIsMutating] = useState(false);

    const aliasKeys = useMemo(() => aliasDocs.map((doc) => doc.id).sort(), [aliasDocs]);
    const aliasName = aliasInput.trim();
    const canAdd = !!aliasName && !isMutating && !readOnly;

    const handleAdd = async () => {
        if (!canAdd) return;
        setIsMutating(true);
        try {
            await onAddAlias(aliasName);
            setAliasInput('');
        } catch {
            // Toast handled by the page; keep the input so the admin can adjust.
        } finally {
            setIsMutating(false);
        }
    };

    const handleRemove = async (alias: string) => {
        setIsMutating(true);
        try {
            await onRemoveAlias(alias);
        } finally {
            setIsMutating(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader>
                    <CardTitle>Alias names</CardTitle>
                    <CardDescription>
                        Alternate names that resolve to this brand — company renames, marketing names, house brands.
                        OCR-misread variants are covered automatically.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <div className="flex gap-2">
                        <Input
                            placeholder="Add an alias (e.g. General Electric)"
                            value={aliasInput}
                            onChange={(e) => setAliasInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canAdd) {
                                    void handleAdd();
                                }
                            }}
                            disabled={isMutating}
                        />
                        <Button onClick={handleAdd} disabled={!canAdd}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add
                        </Button>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead className="w-[140px]">Kind</TableHead>
                                <TableHead className="w-[220px] text-right" aria-label="Actions" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {brand.aliases.map((alias) => {
                                const isCanonical = normalizeBrandName(alias).key === brand.canonicalKey;
                                return (
                                    <TableRow key={alias}>
                                        <TableCell className="font-medium">{alias}</TableCell>
                                        <TableCell>
                                            {isCanonical ? <Badge variant="secondary">Canonical</Badge> : 'Alias'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {!isCanonical && !readOnly && lineNameFromAlias(brand, alias) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => void onAliasToLine(alias)}
                                                    disabled={isMutating}
                                                    title="This is a product line of the brand, not another name for it"
                                                >
                                                    <SplitSquareHorizontal className="mr-1 h-4 w-4" />
                                                    Make it a line
                                                </Button>
                                            )}
                                            {!isCanonical && !readOnly && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => void handleRemove(alias)}
                                                    disabled={isMutating}
                                                    aria-label={`Remove alias ${alias}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Resolvable keys ({aliasKeys.length})</CardTitle>
                    <CardDescription>
                        The exact `brand-aliases` lookup docs pointing here — folded names plus their OCR-confusable
                        variants.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {aliasKeys.length === 0 ? (
                        <div className="section-description">
                            No lookup docs yet — older brands predate the alias projection; adding an alias (or
                            re-creating via Add Brand) will mint them.
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {aliasKeys.map((key) => (
                                <Badge key={key} variant="outline" className="font-mono">
                                    {key}
                                </Badge>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
